// Context: some domains don't allow the terms "SEL" / "Social Emotional Learning" in their schools
// These domains are identified by belonging to specific content profiles
// This mixin provides utilities to prevent those terms from being displayed to users in those content profiles

import { CONTENT_TYPES } from '@/util/access_visibility'
import { getAreasOfNeed } from '@/presentlevels/api_v1.js'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { storeToRefs } from 'pinia'

export const SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS = 'Behavior & SEL'
export const SEL_DEFAULT_AREA_PLW = 'Social & Emotional Learning'
export const SEL_REPLACEMENT_STRATEGIES_PLW = 'Social Skills'
export const SEL_REPLACEMENT_RESOURCES = 'Behavior & Social Skills'

export const SEL_REPLACEMENT_BEHAVIOR_SKILLS = 'Behavior Skills'
export const SEL_REPLACEMENT_RESILIENCY_SKILLS = 'Resiliency Skills'
export const SEL_REPLACEMENT_SOCIAL_SKILLS_INSTRUCTION = 'Social Skills Instruction'

export const SEL_REPLACEMENT_GOALS = [
  SEL_REPLACEMENT_BEHAVIOR_SKILLS,
  SEL_REPLACEMENT_RESILIENCY_SKILLS,
  SEL_REPLACEMENT_SOCIAL_SKILLS_INSTRUCTION
]

const SEL_REPLACEMENT_GOALS_BY_PROFILE = new Map([
  ['florida_no_SEL', SEL_REPLACEMENT_RESILIENCY_SKILLS],
  ['florida_no_share_no_SEL', SEL_REPLACEMENT_RESILIENCY_SKILLS],
  ['arizona_deervalley_no_SEL', SEL_REPLACEMENT_SOCIAL_SKILLS_INSTRUCTION]
])

export function getSELReplacementGoals(contentProfile) {
  return SEL_REPLACEMENT_GOALS_BY_PROFILE.get(contentProfile) ?? SEL_REPLACEMENT_BEHAVIOR_SKILLS
}

export function getSubjectLabelFromValueByContentType(contentType, subject) {
  // cannot be defined outside of a function in a composable because pinia is not registered yet
  const { selVisibility } = storeToRefs(useVisibilityStore())
  const { profileId } = storeToRefs(useContentProfileStore())

  const isSelSubject = subject === SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS || subject === SEL_DEFAULT_AREA_PLW
  const canSeeSELOrIsNotSELSubject = !isSelSubject || selVisibility.value
  if (canSeeSELOrIsNotSELSubject) {
    return subject
  }
  switch (contentType) {
    case CONTENT_TYPES.GOAL:
    case CONTENT_TYPES.SAVED_GOAL:
    case CONTENT_TYPES.SUPER_PAGE:
    case CONTENT_TYPES.SAVED_SUPER_PAGE:
      return getSELReplacementGoals(profileId.value)
    case CONTENT_TYPES.STRATEGY:
      return SEL_REPLACEMENT_STRATEGIES_PLW
    case CONTENT_TYPES.RESOURCE:
      return SEL_REPLACEMENT_RESOURCES
    default:
      return SEL_REPLACEMENT_STRATEGIES_PLW
  }
}

export function getGoalSubjectLabelFromValue(value) {
  return getSubjectLabelFromValueByContentType(CONTENT_TYPES.GOAL, value)
}
export function getStrategySubjectLabelFromValue(value) {
  return getSubjectLabelFromValueByContentType(CONTENT_TYPES.STRATEGY, value)
}
export function getResourceSubjectLabelFromValue(value) {
  return getSubjectLabelFromValueByContentType(CONTENT_TYPES.RESOURCE, value)
}
export function getPlAreaLabelFromValue(value) {
  // cannot be defined outside of a function in a composable because pinia is not registered yet
  const { selVisibility } = storeToRefs(useVisibilityStore())

  let subjects = getAreasOfNeed({ selBlocked: !selVisibility })
  let subject = subjects.find((area) => area.value == value)
  return subject?.label
}
// subject/area label arrays
export function getSubjectLabelsFromValuesByContentType(contentType, subjectValues) {
  return subjectValues.map((subjectValue) =>
    getSubjectLabelFromValueByContentType(contentType, subjectValue)
  )
}

export function getSubjectLabelByContentType(contentType, subject) {
  return getSubjectLabelsFromValuesByContentType(contentType, [subject])[0]
}

export function getResourceSubjectLabelsFromValues(subjectValues) {
  return getSubjectLabelsFromValuesByContentType(CONTENT_TYPES.RESOURCE, subjectValues)
}
export function getPlAreaLabelsFromValues(areaValues) {
  return areaValues?.map((areaValue) => getPlAreaLabelFromValue(areaValue)) || []
}

export function deduplicateNoSELSubjects(subjects) {
  if (subjects.includes(SEL_DEFAULT_SUBJECT_STRATEGIES_GOALS)) {
    return subjects.filter(subject => !SEL_REPLACEMENT_GOALS.includes(subject));
  }
  return subjects;
}
